/**
 * Everything the site needs to point a parent at a download.
 *
 * The version comes from `releases.generated.ts`, which
 * `scripts/set-release-version.cjs` writes at the same time it stamps
 * apps/desktop/package.json and the HA add-on manifest — so the number
 * on the page can't drift from the artifact that was actually built.
 *
 * Binaries live in the PUBLIC `goodshowhq-releases` repo (this
 * monorepo is private, so its releases aren't downloadable).
 *
 * Download URLs use GitHub's `/releases/latest/download/<asset>`
 * redirect, which always resolves to the newest PUBLISHED release
 * (drafts and pre-releases are skipped). That means these links are
 * PERMANENT — publishing a new release updates every download here with
 * no site rebuild or redeploy. For that to work the asset names must be
 * version-LESS, so the `artifactName` templates in
 * apps/desktop/electron-builder.yml drop `${version}` — change one,
 * change the other.
 *
 * `RELEASE_VERSION` no longer drives the URLs; it's only the fallback
 * for the version LABEL (see `fetchLatestVersion`).
 */
import {RELEASE_VERSION} from './releases.generated';

export {RELEASE_VERSION};

const RELEASES_REPO = 'https://github.com/a7medkamel/goodshowhq-releases';
const ASSET_BASE = `${RELEASES_REPO}/releases/latest/download`;

/** electron-builder's `${name}` — apps/desktop/package.json "name". */
const APP = 'goodshowhq-desktop';

export type PlatformId = 'mac-apple-silicon' | 'mac-intel' | 'windows' | 'linux';

export interface DesktopDownload {
  id: PlatformId;
  /** Shown on the button. */
  label: string;
  /** Disambiguates same-OS variants ("Apple Silicon" vs "Intel"). */
  detail: string;
  url: string;
}

export const DESKTOP_DOWNLOADS: DesktopDownload[] = [
  {
    id: 'mac-apple-silicon',
    label: 'Download for macOS',
    detail: 'Apple Silicon (M1–M4)',
    url: `${ASSET_BASE}/${APP}-arm64.dmg`,
  },
  {
    id: 'mac-intel',
    label: 'Download for macOS',
    detail: 'Intel',
    url: `${ASSET_BASE}/${APP}-x64.dmg`,
  },
  {
    id: 'windows',
    label: 'Download for Windows',
    detail: 'Windows 10 & 11 · installer',
    url: `${ASSET_BASE}/${APP}-setup.exe`,
  },
  {
    id: 'linux',
    label: 'Download for Linux',
    detail: 'AppImage · x64',
    url: `${ASSET_BASE}/${APP}.AppImage`,
  },
];

/** Debian/Ubuntu/Raspberry Pi OS alternative to the AppImage. */
export const LINUX_DEB_URL = `${ASSET_BASE}/${APP}_amd64.deb`;

/** All releases, for the "other downloads / changelog" link. */
export const ALL_RELEASES_URL = `${RELEASES_REPO}/releases`;

/**
 * The version LABEL shown on the page. URLs are permanent (latest
 * redirect), but the on-page number would otherwise be frozen at
 * whatever `RELEASE_VERSION` was compiled in. Ask GitHub for the latest
 * published tag so the label self-updates the moment a release is
 * published — with `RELEASE_VERSION` as the offline/rate-limited
 * fallback so the page never shows a blank or a wrong-direction guess.
 */
export async function fetchLatestVersion(
  signal?: AbortSignal,
): Promise<string> {
  try {
    const res = await fetch(
      'https://api.github.com/repos/a7medkamel/goodshowhq-releases/releases/latest',
      {headers: {Accept: 'application/vnd.github+json'}, signal},
    );
    if (!res.ok) return RELEASE_VERSION;
    const data: {tag_name?: string} = await res.json();
    const tag = data.tag_name?.trim();
    // Tags are `v1.2.3`; the label wants the bare number.
    return tag ? tag.replace(/^v/, '') : RELEASE_VERSION;
  } catch {
    return RELEASE_VERSION;
  }
}

/**
 * The Home Assistant add-on isn't a file download — the Supervisor
 * installs it from a repository URL. `my.home-assistant.io` deep-links
 * into the user's OWN HA instance (it never sees their data; it just
 * redirects), which turns a five-step copy-paste into one click.
 */
export const HA_ADDON_REPO_URL =
  'https://github.com/a7medkamel/goodshowhq-ha-addons';
export const HA_ADDON_ADD_REPO_URL = `https://my.home-assistant.io/redirect/supervisor_add_addon_repository/?repository_url=${encodeURIComponent(
  HA_ADDON_REPO_URL,
)}`;

/**
 * UA sniff for "which button do I highlight first". Deliberately
 * coarse — everything is always reachable; this only decides the
 * primary CTA.
 */
export function detectPlatform(): PlatformId | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  // Phones get the mobile app, not a desktop binary.
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return null;
  if (/Mac/i.test(ua)) {
    // Apple Silicon Macs report the same UA as Intel ones. The honest
    // tiebreaker is the WebGL renderer string ("Apple M1/M2/..."), and
    // when that's unavailable we default to Apple Silicon — every Mac
    // sold since 2020 is one, and the Intel build is one click away.
    return isAppleSilicon() ? 'mac-apple-silicon' : 'mac-intel';
  }
  if (/Win/i.test(ua)) return 'windows';
  if (/Linux|X11/i.test(ua)) return 'linux';
  return null;
}

function isAppleSilicon(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl');
    if (!gl) return true;
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    if (!info) return true;
    const renderer = String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL));
    if (/Intel/i.test(renderer)) return false;
    return true;
  } catch {
    return true;
  }
}
